import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Archive, ChevronDown, ChevronRight, GitBranch, Loader2, MessageSquarePlus, Pin,
  RefreshCw, Send,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { explain } from '@/shared/api/problem'
import { ErrorStrip, LoadingBlock, Note } from '@/shared/ui/states'
import { fmtRelative } from '@/shared/lib/format'
import { useCan } from '@/shared/auth/session'
import {
  ask, createThread, forkThread, getThread, listThreads, updateThread,
  type Message, type Thread, type ThreadDetail,
} from './api'
import { RUN_STATUS_LABEL, isLiveRun } from './vocab'

/**
 * The AI Data Analyst.
 *
 * ## Every turn here is a full run
 *
 * Asking a question creates an `advisor_run` with its own evidence brief, its own
 * model calls, its own cost and its own report row. That is heavier than a chat
 * endpoint and it is the point: a console that grows a fast unlogged
 * conversational path “because it is just questions” has, within a month, moved
 * the real work onto the path with no audit trail. The link under each answer
 * goes to the council that produced it.
 *
 * ## Retry and fork
 *
 * An answer that misses is the normal case. **Retry** re-asks with an optional
 * steer and appends a new turn — the earlier one stays, collapsed, marked
 * superseded, because a console that silently replaces one answer with another
 * cannot be asked what it said yesterday. **Fork** copies the conversation up to a
 * chosen message into a NEW thread with its own id, so a tangent does not drag
 * the rest of the conversation with it.
 */
export function AnalystPage() {
  const [threads, setThreads] = useState<Thread[] | null>(null)
  const [activeId, setActiveId] = useState<string | null>(null)
  const [detail, setDetail] = useState<ThreadDetail | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [question, setQuestion] = useState('')
  const [busy, setBusy] = useState(false)
  const [retryOf, setRetryOf] = useState<Message | null>(null)
  const mayRun = useCan('advisor:run')
  const bottom = useRef<HTMLDivElement>(null)

  const loadThreads = useCallback(async () => {
    try {
      setError(null)
      const list = await listThreads(false)
      setThreads(list)
      setActiveId((current) => current ?? list[0]?.id ?? null)
    } catch (err) {
      setError(explain(err))
    }
  }, [])

  const loadDetail = useCallback(async (id: string) => {
    try {
      setDetail(await getThread(id))
    } catch (err) {
      setError(explain(err))
    }
  }, [])

  useEffect(() => { void loadThreads() }, [loadThreads])
  useEffect(() => { if (activeId) void loadDetail(activeId) }, [activeId, loadDetail])

  // Poll while a turn is still being worked. The assistant bubble fills itself in
  // from the run's report the moment it completes.
  const waiting = (detail?.messages ?? []).some(
    (m) => m.runStatus != null && isLiveRun(m.runStatus))
  useEffect(() => {
    if (!waiting || !activeId) return
    const timer = setInterval(() => { void loadDetail(activeId) }, 3000)
    return () => clearInterval(timer)
  }, [waiting, activeId, loadDetail])

  useEffect(() => {
    bottom.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [detail?.messages.length])

  const newThread = async () => {
    try {
      const thread = await createThread({})
      await loadThreads()
      setActiveId(thread.id)
      setDetail({ thread, messages: [] })
    } catch (err) {
      setError(explain(err))
    }
  }

  const send = async () => {
    if (!activeId || !question.trim()) return
    setBusy(true)
    setError(null)
    try {
      await ask(activeId, {
        question: retryOf ? retryOf.content : question.trim(),
        retryOf: retryOf?.id,
        retryNote: retryOf ? question.trim() : undefined,
      })
      setQuestion('')
      setRetryOf(null)
      await loadDetail(activeId)
      await loadThreads()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  const fork = async (message: Message) => {
    if (!activeId) return
    try {
      const branch = await forkThread(activeId, message.id)
      await loadThreads()
      setActiveId(branch.id)
    } catch (err) {
      setError(explain(err))
    }
  }

  return (
    <div className="grid gap-4 lg:grid-cols-[16rem_1fr]">
      {/* ── conversations ────────────────────────────────────────────────── */}
      <aside className="space-y-2">
        <button type="button" onClick={() => void newThread()}
          className="erp-btn h-9 w-full px-3 text-erp-sm inline-flex items-center
            justify-center gap-2">
          <MessageSquarePlus className="h-3.5 w-3.5" /> New conversation
        </button>

        {threads === null && <LoadingBlock />}
        {threads && threads.length === 0 && (
          <p className="text-erp-xs text-muted-foreground px-1">
            No conversations yet.
          </p>
        )}
        <ul className="space-y-1">
          {(threads ?? []).map((thread) => (
            <li key={thread.id}>
              <button type="button" onClick={() => setActiveId(thread.id)}
                className={cn('w-full rounded-md border px-2.5 py-1.5 text-left',
                  thread.id === activeId
                    ? 'border-chocolate/60 bg-chocolate-soft/30'
                    : 'border-border hover:border-chocolate/40')}>
                <div className="flex items-center gap-1.5">
                  {thread.forkedFromThreadId && (
                    <GitBranch className="h-3 w-3 shrink-0 text-muted-foreground" />
                  )}
                  {thread.pinned && <Pin className="h-3 w-3 shrink-0 text-chocolate" />}
                  <span className="truncate text-erp-xs">{thread.title}</span>
                </div>
                <div className="text-erp-xs text-muted-foreground/70 mt-0.5">
                  {thread.messageCount} turns · {fmtRelative(thread.lastMessageAt)}
                </div>
              </button>
            </li>
          ))}
        </ul>
      </aside>

      {/* ── the conversation ─────────────────────────────────────────────── */}
      <section className="min-w-0 space-y-3">
        <ErrorStrip message={error} className="rounded-md border" />

        {detail && (
          <>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                <h1 className="text-erp-sm font-semibold truncate">{detail.thread.title}</h1>
                {detail.thread.forkedFromThreadId && (
                  <p className="text-erp-xs text-muted-foreground">
                    Branched from another conversation. Both keep their own history.
                  </p>
                )}
              </div>
              <div className="flex gap-1.5">
                <button type="button"
                  onClick={() => void updateThread(detail.thread.id,
                    { pinned: !detail.thread.pinned }).then(loadThreads)}
                  className="erp-btn h-8 px-2.5 text-erp-xs inline-flex items-center gap-1.5">
                  <Pin className="h-3.5 w-3.5" />
                  {detail.thread.pinned ? 'Unpin' : 'Pin'}
                </button>
                <button type="button"
                  onClick={() => void updateThread(detail.thread.id, { archived: true })
                    .then(() => { setActiveId(null); setDetail(null); return loadThreads() })}
                  className="erp-btn h-8 px-2.5 text-erp-xs inline-flex items-center gap-1.5">
                  <Archive className="h-3.5 w-3.5" /> Archive
                </button>
                <button type="button"
                  onClick={() => void loadDetail(detail.thread.id)}
                  className="erp-btn h-8 px-2.5 text-erp-xs inline-flex items-center gap-1.5">
                  <RefreshCw className="h-3.5 w-3.5" /> Refresh
                </button>
              </div>
            </div>

            {detail.messages.length === 0 && (
              <Note tone="info">
                Ask about anything this platform holds — turnout in a ward, where transfers are
                stuck, which programmes are returning least. Every answer names the dataset it
                came from, and says so plainly when the platform does not hold what you asked
                for.
              </Note>
            )}

            <ol className="space-y-2">
              {detail.messages.map((message) => (
                <MessageBubble key={message.id} message={message}
                  onRetry={() => { setRetryOf(message); setQuestion('') }}
                  onFork={() => void fork(message)} />
              ))}
            </ol>
            <div ref={bottom} />

            {retryOf && (
              <Note tone="info" onDismiss={() => setRetryOf(null)}>
                <span className="font-medium">Re-asking. </span>
                The previous answer stays in the thread, collapsed. Say what was wrong with it
                below and it will be passed on as a steer.
              </Note>
            )}

            <div className="flex gap-2">
              <textarea
                rows={2}
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void send()
                }}
                disabled={!mayRun}
                placeholder={retryOf
                  ? 'What was wrong with that answer?'
                  : mayRun
                    ? 'Ask about this platform’s data…  (⌘/Ctrl + Enter to send)'
                    : 'Asking needs advisor:run'}
                className="erp-input flex-1 text-erp-sm" />
              <button type="button" disabled={busy || !mayRun || !question.trim()}
                onClick={() => void send()}
                className="erp-btn erp-btn-primary h-auto px-3 text-erp-sm inline-flex
                  items-center gap-2">
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : <Send className="h-3.5 w-3.5" />}
                {retryOf ? 'Re-ask' : 'Ask'}
              </button>
            </div>
          </>
        )}
      </section>
    </div>
  )
}

function MessageBubble({ message, onRetry, onFork }: {
  message: Message
  onRetry: () => void
  onFork: () => void
}) {
  const [open, setOpen] = useState(!message.superseded)
  const isUser = message.role === 'USER'
  const working = message.runStatus != null && isLiveRun(message.runStatus)

  if (message.superseded && !open) {
    return (
      <li>
        <button type="button" onClick={() => setOpen(true)}
          className="flex w-full items-center gap-1.5 rounded-md border border-dashed
            border-border px-2.5 py-1.5 text-left text-erp-xs text-muted-foreground">
          <ChevronRight className="h-3 w-3" />
          An earlier answer to this, kept — it was re-asked
        </button>
      </li>
    )
  }

  return (
    <li className={cn('rounded-md border px-3 py-2',
      isUser ? 'border-border bg-muted/30'
        : message.role === 'SYSTEM'
          ? 'border-amber-200 bg-amber-50/50 dark:bg-amber-500/5'
          : 'border-border')}>
      <div className="flex items-center gap-2 text-erp-xs text-muted-foreground">
        <span className="font-medium">
          {isUser ? 'You' : message.role === 'SYSTEM' ? 'Axis' : 'Analyst'}
        </span>
        {working && (
          <span className="inline-flex items-center gap-1 text-chocolate">
            <Loader2 className="h-3 w-3 animate-spin" />
            {RUN_STATUS_LABEL[message.runStatus!] ?? message.runStatus}
          </span>
        )}
        {message.superseded && (
          <button type="button" onClick={() => setOpen(false)}
            className="inline-flex items-center gap-1">
            <ChevronDown className="h-3 w-3" /> superseded — collapse
          </button>
        )}
        <span className="ml-auto">{fmtRelative(message.createdAt)}</span>
      </div>

      <p className="mt-1 text-erp-sm whitespace-pre-wrap">{message.content}</p>

      {message.retryNote && (
        <p className="mt-1 text-erp-xs text-muted-foreground">
          Steer given: “{message.retryNote}”
        </p>
      )}

      {!isUser && (
        <div className="mt-1.5 flex flex-wrap items-center gap-2 text-erp-xs">
          {message.runId && (
            <Link to={`/advisory/runs/${message.runId}`}
              className="text-muted-foreground hover:text-foreground">
              the council behind this answer
            </Link>
          )}
          {message.reportId && (
            <Link to={`/advisory/reports/${message.reportId}`}
              className="text-muted-foreground hover:text-foreground">
              full report
            </Link>
          )}
          {!working && (
            <>
              <button type="button" onClick={onRetry}
                className="text-muted-foreground hover:text-foreground">
                ask again
              </button>
              <button type="button" onClick={onFork}
                className="inline-flex items-center gap-1 text-muted-foreground
                  hover:text-foreground">
                <GitBranch className="h-3 w-3" /> branch from here
              </button>
            </>
          )}
        </div>
      )}
    </li>
  )
}
